import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axiosInstance';
import MyIssues from './MyIssues';
import IssueDetail from './IssueDetail';
import CreateIssue from './CreateIssue';
import SearchIssues from './SearchIssues';
import ProjectTree from './ProjectTree';
import CustomSelect from './CustomSelect';
import './RedminePortal.css';

const API_BASE = '/redmine-portal';

function RedminePortal() {

  // Active tab
  const [activeTab, setActiveTab] = useState('my-issues');

  // Issues state
  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueDetail, setIssueDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [filter, setFilter] = useState('all'); // all, assigned, created, watching
  const [statusFilter, setStatusFilter] = useState('open'); // open, *, closed
  const [projectFilter, setProjectFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Metadata
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  // Unread count
  const [unreadCount, setUnreadCount] = useState(0);

  // Load metadata on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [statusRes, priorityRes, trackerRes, projectRes, usersRes] = await Promise.all([
          axiosInstance.get(`${API_BASE}/statuses`),
          axiosInstance.get(`${API_BASE}/priorities`),
          axiosInstance.get(`${API_BASE}/trackers`),
          axiosInstance.get(`${API_BASE}/projects`),
          axiosInstance.get(`${API_BASE}/users`),
        ]);
        setStatuses(statusRes.data.statuses || []);
        setPriorities(priorityRes.data.priorities || []);
        setTrackers(trackerRes.data.trackers || []);
        setProjects(projectRes.data.projects || []);
        setUsers(usersRes.data.users || []);
      } catch (err) {
        console.error('Error loading metadata:', err);
      }
    };
    loadMetadata();
  }, []);

  // Load issues
  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        filter,
        status_id: statusFilter === 'open' ? 'open' : statusFilter,
      });
      if (projectFilter) params.append('project_id', projectFilter);
      if (priorityFilter) params.append('priority_id', priorityFilter);

      const res = await axiosInstance.get(`${API_BASE}/my-issues?${params}`);
      setIssues(res.data.issues || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, projectFilter, priorityFilter]);

  useEffect(() => {
    if (activeTab === 'my-issues') {
      loadIssues();
    }
  }, [activeTab, loadIssues]);

  // Load issue detail
  const loadIssueDetail = useCallback(async (issueId) => {
    try {
      const res = await axiosInstance.get(`${API_BASE}/issues/${issueId}`);
      setIssueDetail(res.data);
      // Decrease unread count if this was unread
      const issue = issues.find(i => i.id === issueId);
      if (issue?._unread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        setIssues(prev => prev.map(i =>
          i.id === issueId ? { ...i, _unread: false } : i
        ));
      }
    } catch (err) {
      console.error('Error loading issue detail:', err);
    }
  }, [issues]);

  useEffect(() => {
    if (selectedIssue) {
      loadIssueDetail(selectedIssue);
    } else {
      setIssueDetail(null);
    }
  }, [selectedIssue, loadIssueDetail]);

  // Handle issue update (reply, status change)
  const handleIssueUpdate = async (issueId, updateData) => {
    try {
      await axiosInstance.put(`${API_BASE}/issues/${issueId}`, updateData);
      // Reload issue detail
      loadIssueDetail(issueId);
      // Refresh list
      loadIssues();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Update failed' };
    }
  };

  // Handle issue creation
  const handleCreateIssue = async (issueData) => {
    try {
      const res = await axiosInstance.post(`${API_BASE}/issues`, issueData);
      setActiveTab('my-issues');
      loadIssues();
      setSelectedIssue(res.data.issue.id);
      return { success: true, issue: res.data.issue };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Create failed' };
    }
  };

  // Status badge color helper
  const getStatusColor = (statusName) => {
    const name = (statusName || '').toLowerCase();
    if (name.includes('new')) return '#3498db';
    if (name.includes('progress') || name.includes('assigned')) return '#f39c12';
    if (name.includes('resolved') || name.includes('closed')) return '#27ae60';
    if (name.includes('feedback')) return '#9b59b6';
    if (name.includes('rejected')) return '#e74c3c';
    return '#95a5a6';
  };

  // Priority icon helper
  const getPriorityIcon = (priorityName) => {
    const name = (priorityName || '').toLowerCase();
    if (name.includes('immediate')) return { icon: '!!!', color: '#c0392b' };
    if (name.includes('urgent')) return { icon: '!!', color: '#e74c3c' };
    if (name.includes('high')) return { icon: '!', color: '#f39c12' };
    if (name.includes('low')) return { icon: '', color: '#3498db' };
    return { icon: '', color: '#27ae60' };
  };

  return (
    <div className="redmine-portal">
      {/* Header */}
      <div className="redmine-header">
        <h1>Redmine Portal</h1>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount} unread</span>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="redmine-tabs">
        <button
          className={`tab-btn ${activeTab === 'my-issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-issues')}
        >
          My Issues
          {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
        <button
          className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Create Issue
        </button>
        <button
          className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          Search
        </button>
        <button
          className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          Projects
        </button>
      </div>

      {/* Content */}
      <div className="redmine-content">
        {activeTab === 'my-issues' && (
          <div className="issues-layout">
            {/* Left: Filters + List */}
            <div className="issues-list-panel">
              {/* Filters */}
              <div className="issue-filters">
                <div className="filter-row">
                  <CustomSelect
                    value={filter}
                    onChange={setFilter}
                    options={[
                      { value: 'all', label: 'All My Issues' },
                      { value: 'assigned', label: 'Assigned to Me' },
                      { value: 'created', label: 'Created by Me' },
                      { value: 'watching', label: 'Watching' },
                    ]}
                  />

                  <CustomSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: '*', label: 'All Statuses' },
                      { value: 'closed', label: 'Closed' },
                    ]}
                  />

                  <button className="btn-refresh" onClick={loadIssues}>
                    Refresh
                  </button>
                </div>
              </div>

              {/* Issue List */}
              <MyIssues
                issues={issues}
                loading={loading}
                error={error}
                selectedIssue={selectedIssue}
                onSelectIssue={setSelectedIssue}
                getStatusColor={getStatusColor}
                getPriorityIcon={getPriorityIcon}
              />
            </div>

            {/* Right: Detail Panel */}
            <div className="issue-detail-panel">
              {issueDetail ? (
                <IssueDetail
                  issue={issueDetail}
                  statuses={statuses}
                  priorities={priorities}
                  trackers={trackers}
                  users={users}
                  onUpdate={handleIssueUpdate}
                  onClose={() => setSelectedIssue(null)}
                  getStatusColor={getStatusColor}
                  getPriorityIcon={getPriorityIcon}
                />
              ) : (
                <div className="no-issue-selected">
                  <p>Select an issue to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'create' && (
          <CreateIssue
            projects={projects}
            trackers={trackers}
            priorities={priorities}
            onSubmit={handleCreateIssue}
          />
        )}

        {activeTab === 'search' && (
          <SearchIssues
            onSelectIssue={(issueId) => {
              setSelectedIssue(issueId);
              setActiveTab('my-issues');
            }}
            getStatusColor={getStatusColor}
            getPriorityIcon={getPriorityIcon}
          />
        )}

        {activeTab === 'projects' && (
          <ProjectTree
            projects={projects}
            onSelectProject={(projectId) => {
              setProjectFilter(projectId.toString());
              setActiveTab('my-issues');
            }}
          />
        )}
      </div>
    </div>
  );
}

export default RedminePortal;
